/**
 * Skills Store (Zustand)
 *
 * Global skills state management
 * - Skills config keyed by group_name with loading/error states
 * - Assessments keyed by rider_id with loading/error states
 * - CRUD actions for fetching config, assessments, and creating assessments
 * - Cache invalidation for config
 *
 * Requirements: 2.1, 2.2, 9.3, 9.4
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { restClient } from '../api/rest-client';
import type { SkillConfig, SkillAssessment, CreateSkillAssessmentDto, ThresholdSettings, TimeTrialZoneResponse } from '../types/skills';

interface SkillsState {
  // Config state (admin)
  skillsConfig: Record<string, SkillConfig[]>; // keyed by group_name
  configLoading: boolean;
  configError: string | null;

  // Assessment state (per rider)
  assessments: Record<string, SkillAssessment[]>; // keyed by rider_id
  assessmentsLoading: boolean;
  assessmentsError: string | null;

  // Threshold state
  thresholds: ThresholdSettings | null;
  thresholdsLoading: boolean;
  thresholdsError: string | null;

  // Time-trial zone state (per rider)
  timeTrialZone: Record<string, TimeTrialZoneResponse>;
  timeTrialZoneLoading: boolean;
  timeTrialZoneError: string | null;

  // Actions
  fetchSkillsConfig: (groupName: string) => Promise<void>;
  fetchAllSkillsConfig: () => Promise<void>;
  fetchAssessments: (riderId: string) => Promise<void>;
  createAssessment: (assessment: CreateSkillAssessmentDto) => Promise<void>;
  invalidateConfig: (groupName?: string) => void;
  fetchThresholds: () => Promise<void>;
  updateThresholds: (settings: ThresholdSettings) => Promise<void>;
  fetchTimeTrialZone: (riderId: string, group: string) => Promise<void>;
}

// In-flight promise for deduplicating concurrent fetchThresholds calls
let thresholdsFetchPromise: Promise<void> | null = null;

/**
 * Skills Store
 *
 * Manages skills config and assessment state globally using Zustand
 */
export const useSkillsStore = create<SkillsState>()(
  devtools(
    (set, _get) => ({
      // Initial state
      skillsConfig: {},
      configLoading: false,
      configError: null,
      assessments: {},
      assessmentsLoading: false,
      assessmentsError: null,
      thresholds: null,
      thresholdsLoading: false,
      thresholdsError: null,
      timeTrialZone: {},
      timeTrialZoneLoading: false,
      timeTrialZoneError: null,

      /**
       * Fetch skills config for a specific group.
       * Skips the API call if config for this group is already cached.
       * Requirements: 9.3, 9.4
       */
      fetchSkillsConfig: async (groupName: string) => {
        // Skip if already cached
        if (_get().skillsConfig[groupName]?.length) return;

        set({ configLoading: true, configError: null });

        try {
          const skills = await restClient.getSkillsConfig(groupName);

          set(state => ({
            skillsConfig: { ...state.skillsConfig, [groupName]: skills },
            configLoading: false,
            configError: null,
          }));
        } catch (error) {
          console.error('Failed to fetch skills config:', error);
          set({
            configError: error instanceof Error ? error.message : 'Failed to fetch skills config',
            configLoading: false,
          });
        }
      },

      /**
       * Fetch all skills config across all groups (admin use)
       * Requirements: 9.3
       */
      fetchAllSkillsConfig: async () => {
        set({ configLoading: true, configError: null });

        try {
          const allSkills = await restClient.getSkillsConfig();

          // Group skills by group_name
          const grouped: Record<string, SkillConfig[]> = {};
          for (const skill of allSkills) {
            if (!grouped[skill.group_name]) {
              grouped[skill.group_name] = [];
            }
            grouped[skill.group_name].push(skill);
          }

          set({
            skillsConfig: grouped,
            configLoading: false,
            configError: null,
          });
        } catch (error) {
          console.error('Failed to fetch all skills config:', error);
          set({
            configError: error instanceof Error ? error.message : 'Failed to fetch all skills config',
            configLoading: false,
          });
        }
      },

      /**
       * Fetch all assessments for a rider.
       * Skips the API call if assessments for this rider are already cached.
       * Requirements: 2.1
       */
      fetchAssessments: async (riderId: string) => {
        // Skip if already cached
        if (_get().assessments[riderId]) return;

        set({ assessmentsLoading: true, assessmentsError: null });

        try {
          const riderAssessments = await restClient.getSkillsAssessments(riderId);

          set(state => ({
            assessments: { ...state.assessments, [riderId]: riderAssessments },
            assessmentsLoading: false,
            assessmentsError: null,
          }));
        } catch (error) {
          console.error('Failed to fetch assessments:', error);
          set({
            assessmentsError: error instanceof Error ? error.message : 'Failed to fetch assessments',
            assessmentsLoading: false,
          });
        }
      },

      /**
       * Create a new assessment for a rider's skill
       * Requirements: 2.2
       */
      createAssessment: async (assessment: CreateSkillAssessmentDto) => {
        // NOTE: We intentionally do NOT set assessmentsLoading here.
        // This is an append operation — setting assessmentsLoading would replace
        // the entire modal content with a spinner, destroying scroll position.
        set({ assessmentsError: null });

        try {
          const created = await restClient.createSkillAssessment(assessment);

          // Append to existing assessments for this rider
          set(state => {
            const existing = state.assessments[assessment.rider_id] || [];
            return {
              assessments: {
                ...state.assessments,
                [assessment.rider_id]: [...existing, created],
              },
              assessmentsError: null,
            };
          });
        } catch (error) {
          console.error('Failed to create assessment:', error);
          set({
            assessmentsError: error instanceof Error ? error.message : 'Failed to create assessment',
          });
          throw error;
        }
      },

      /**
       * Invalidate cached config for a group or all groups
       * Requirements: 9.3
       */
      invalidateConfig: (groupName?: string) => {
        if (groupName) {
          set(state => {
            const { [groupName]: _, ...rest } = state.skillsConfig;
            return { skillsConfig: rest };
          });
        } else {
          set({ skillsConfig: {} });
        }
      },

      /**
       * Fetch threshold settings, caching the result and deduplicating concurrent calls.
       * Requirements: 2.2, 3.1
       */
      fetchThresholds: async () => {
        // Return cached result if already fetched
        if (_get().thresholds !== null) return;

        // Deduplicate concurrent calls
        if (thresholdsFetchPromise) {
          await thresholdsFetchPromise;
          return;
        }

        set({ thresholdsLoading: true, thresholdsError: null });

        thresholdsFetchPromise = (async () => {
          try {
            const settings = await restClient.getThresholdSettings();
            set({ thresholds: settings, thresholdsLoading: false, thresholdsError: null });
          } catch (error) {
            console.error('Failed to fetch thresholds:', error);
            set({
              thresholdsError: error instanceof Error ? error.message : 'Failed to fetch thresholds',
              thresholdsLoading: false,
            });
          } finally {
            thresholdsFetchPromise = null;
          }
        })();

        await thresholdsFetchPromise;
      },

      /**
       * Update threshold settings via the API and update state on success.
       * Throws on error so callers can handle revert logic.
       * Requirements: 2.6
       */
      updateThresholds: async (settings: ThresholdSettings) => {
        try {
          const saved = await restClient.updateThresholdSettings(settings);
          set({ thresholds: saved, thresholdsError: null });
        } catch (error) {
          console.error('Failed to update thresholds:', error);
          set({
            thresholdsError: error instanceof Error ? error.message : 'Failed to update thresholds',
          });
          throw error;
        }
      },

      /**
       * Fetch time-trial zone assessment for a rider.
       * Skips the API call if zone data for this rider is already cached.
       * Requirements: 4.1, 4.2
       */
      fetchTimeTrialZone: async (riderId: string, group: string) => {
        // Skip if already cached
        if (_get().timeTrialZone[riderId]) return;

        set({ timeTrialZoneLoading: true, timeTrialZoneError: null });

        try {
          const result = await restClient.getTimeTrialZone(riderId, group);

          set(state => ({
            timeTrialZone: { ...state.timeTrialZone, [riderId]: result },
            timeTrialZoneLoading: false,
            timeTrialZoneError: null,
          }));
        } catch (error) {
          console.error('Failed to fetch time-trial zone:', error);
          set({
            timeTrialZoneError: error instanceof Error ? error.message : 'Failed to fetch time-trial zone',
            timeTrialZoneLoading: false,
          });
        }
      },
    }),
    { name: 'SkillsStore' }
  )
);

// Selectors for convenience
export const selectSkillsConfig = (state: SkillsState) => state.skillsConfig;
export const selectConfigLoading = (state: SkillsState) => state.configLoading;
export const selectConfigError = (state: SkillsState) => state.configError;
export const selectAssessments = (state: SkillsState) => state.assessments;
export const selectAssessmentsLoading = (state: SkillsState) => state.assessmentsLoading;
export const selectAssessmentsError = (state: SkillsState) => state.assessmentsError;
export const selectThresholds = (state: SkillsState) => state.thresholds;
export const selectThresholdsLoading = (state: SkillsState) => state.thresholdsLoading;
export const selectThresholdsError = (state: SkillsState) => state.thresholdsError;
export const selectTimeTrialZone = (state: SkillsState) => state.timeTrialZone;
export const selectTimeTrialZoneLoading = (state: SkillsState) => state.timeTrialZoneLoading;
export const selectTimeTrialZoneError = (state: SkillsState) => state.timeTrialZoneError;

export default useSkillsStore;
